interface URLObject {
	inner: string;
}

export type FetchMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
export type SerializableKey = string;
export type SerializableValue = string | number | boolean;
export type SerializableRecord = Record<SerializableKey, SerializableValue>;

export interface FetchOptions {
	/**
	 * Raw string body to send with the request.
	 * Use this when you need to send a custom formatted body (e.g., form data).
	 *
	 * @example
	 * ```javascript
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   body: 'name=value&other_name=other_value'
	 * }).get()
	 * ```
	 */
	body?: string;

	/**
	 * Object to be serialized as JSON and sent as the request body.
	 * When provided, 'Content-Type' and 'accept' headers are set to 'application/json'.
	 *
	 * @example
	 * ```javascript
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   json: { user_id: 123, name: 'John Doe' }
	 * }).post()
	 * ```
	 */
	json?: Record<string, unknown>;

	/**
	 * Query parameters to append to the URL.
	 * Can be provided as an object or as an array of key-value pairs.
	 * Nullish values are automatically filtered out.
	 *
	 * @example
	 * ```javascript
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   params: { page: 1, limit: 10 }
	 * }).get()
	 * // or
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   params: [['page', 1], ['limit', 10]]
	 * }).get()
	 * ```
	 */
	params?: SerializableRecord | [SerializableKey, SerializableValue][];

	/**
	 * HTTP headers to include with the request.
	 * Can be provided as an object or as an array of key-value pairs.
	 *
	 * @example
	 * ```javascript
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   headers: { 'X-Custom-Header': 'value' }
	 * }).get()
	 * // or
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   headers: [['X-Custom-Header', 'value']]
	 * }).get()
	 * ```
	 */
	headers?: Record<string, string> | [string, string][];

	/**
	 * Bearer token for authentication.
	 * When provided, an 'Authorization' header is automatically added with format 'Bearer {token}'.
	 *
	 * @example
	 * ```javascript
	 * const { status, body } = clientFetch(url`/api/protected_resource`, {
	 *   bearer: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
	 * }).get()
	 * ```
	 */
	bearer?: string;

	/**
	 * AbortSignal to abort the fetch request.
	 * Useful for implementing request timeouts or cancellation.
	 *
	 * @example
	 * ```javascript
	 * const controller = new AbortController();
	 *
	 * const { status, body } = clientFetch(url`/api/example/route`, {
	 *   abort_signal: controller.signal
	 * }).get()
	 *
	 * // When cancelling the request
	 * controller.abort() // Abort the fetch request
	 * ```
	 */
	abort_signal?: AbortSignal;
}

export interface FetchReturnObject {
	/**
	 * HTTP status code of the response
	 */
	status: number;
	/**
	 * Whether the response was successful (200-299).
	 *
	 * `true` if the status is between and including 200 and 299, `false` otherwise.
	 */
	ok: boolean;
	/**
	 * Raw body of the response (retrieved by `response.arrayBuffer()`).
	 *
	 * Will be `string` if the conversion from `ArrayBuffer` to `string` was successful,
	 * otherwise remains as `ArrayBuffer`
	 */
	raw_body: string | ArrayBuffer;
	/**
	 * Parsed JSON response body if available, otherwise `null`
	 */
	body: unknown | null;
}

export interface FetchReturn {
	get: () => Promise<FetchReturnObject>;
	post: () => Promise<FetchReturnObject>;
	put: () => Promise<FetchReturnObject>;
	delete: () => Promise<FetchReturnObject>;
	patch: () => Promise<FetchReturnObject>;
}

/**
 * This tag-function ensures that a url is properly encoded, using different strategies
 * for path segments and query parameters:
 * - Path segments (before '?'): encoded with encodeURI to preserve URL structure characters
 * - Query parameters (after '?'): encoded with encodeURIComponent for stricter encoding
 */
export function url(strings: TemplateStringsArray, ...expr: (string | number)[]): URLObject {
	let result = "";
	let isQueryParam = false;

	// Helper function to encode a string based on whether it's a query parameter or path segment
	const encodePart = (str: string, inQuery: boolean): string => {
		if (inQuery) {
			return encodeURIComponent(str);
		}
		return encodeURI(str);
	};

	// Helper function to process any part that might contain a query transition
	const processPart = (
		str: string,
		currentInQuery: boolean,
	): { result: string; is_query: boolean } => {
		// Check if this part contains the transition to query parameters
		if (!currentInQuery && str.includes("?")) {
			const parts = str.split("?", 2);
			const pathPart = parts[0];
			const queryPart = parts.length > 1 ? parts[1] : null;

			// Encode path part
			let resultStr = encodePart(pathPart, false);

			// Handle query part if it exists
			if (queryPart) {
				const queryParams = queryPart.split("&");
				const encodedParams = queryParams.map((param) => {
					if (param.includes("=")) {
						const [key, value] = param.split("=", 2);
						return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
					}
					return encodeURIComponent(param);
				});
				resultStr += `?${encodedParams.join("&")}`;
			} else {
				resultStr += "?";
			}

			return { is_query: true, result: resultStr };
		}

		// Special handling for parts in query parameter section
		if (currentInQuery) {
			// Split by '&' to handle each parameter separately
			const querySegments = str.split("&");
			const encodedSegments = querySegments.map((segment, index) => {
				// Don't prepend '&' to the first segment
				const prefix = index > 0 ? "&" : "";

				if (segment.includes("=")) {
					const [key, value] = segment.split("=", 2);
					return `${prefix}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
				}
				return `${prefix}${encodeURIComponent(segment)}`;
			});

			return { is_query: currentInQuery, result: encodedSegments.join("") };
		}

		return { is_query: currentInQuery, result: encodePart(str, currentInQuery) };
	};

	// Process each part of the template
	for (let i = 0; i < strings.length; i++) {
		// Process static part
		const { result: encodedStatic, is_query: updatedIsQuery } = processPart(
			strings[i],
			isQueryParam,
		);
		result += encodedStatic;
		isQueryParam = updatedIsQuery;

		// Process dynamic part if it exists
		if (i < expr.length) {
			const exprValue = String(expr[i]);
			const { result: encodedExpr, is_query: furtherUpdatedIsQuery } = processPart(
				exprValue,
				isQueryParam,
			);
			result += encodedExpr;
			isQueryParam = furtherUpdatedIsQuery;
		}
	}

	return { inner: result };
}

/**
 * Wrapper for standard fetch client.
 * - `url`, `params`, and `headers` values are all sanitized before usage.
 * - When `json` body is provided, 'Content-Type'-header is set automatically to 'application/json'.
 *
 * @param url URL as a URLObject or string. It is highly recommended to use the `url`-tag function to apply url encoding (see example)
 * @param options Options to be passed to the fetch call (see {@link FetchOptions})
 * @returns Dictionary with items returned from fetch (see {@link FetchReturnObject})
 *
 * @example
 * ```javascript
 * const { status, body } = await clientFetch(url`/shop/concept-order`, {
 * 	json: {
 * 		order_type: 'domain',
 * 		order_dump: {
 * 			SomeKey: 'SomeVal'
 * 		}
 * 	}
 * }).post()
 * ```
 */
export function clientFetch(url: URLObject | string, options?: FetchOptions): FetchReturn {
	let finalUrl: string | undefined;
	if (typeof url === "string") {
		finalUrl = url;
	}

	if (Object.hasOwn(url as URLObject, "inner")) {
		finalUrl = (url as URLObject).inner;
	}

	if (!finalUrl) {
		throw new Error("Did not pass valid url object, please use 'url' tag-function");
	}

	if (options?.params) {
		// Check type of incoming params and parse to single type
		let rawParams: [SerializableKey, SerializableValue][] = [];
		if (Array.isArray(options.params)) {
			// Is already an array
			rawParams = options.params;
		} else {
			// Is an object, thus convert to proper type
			rawParams = Object.entries(options.params);
		}

		// Clean up params (filter out nullish values since these are not filtered by URLSearchParams)
		const cleanParams = serializeKeyValues(rawParams);

		// Construct query string
		const urlQuery = new URLSearchParams();

		for (const [key, value] of cleanParams) {
			// Use append instead of set, to allow for multiple values
			urlQuery.append(key, value.toString());
		}
		// Append the url_query to the final_url.
		// `.toString()` applies url encoding on the items in the url query while converting to a query string
		finalUrl = `${finalUrl}?${urlQuery.toString()}`;
	}

	const outputHeaders: [string, string][] = [];
	if (options?.headers) {
		// Check type of incoming headers and parse to single type
		let rawHeaders: [SerializableKey, SerializableValue][] = [];
		if (Array.isArray(options.headers)) {
			// Is already an array
			rawHeaders = options.headers;
		} else {
			// Is an object, thus convert to proper type
			rawHeaders = Object.entries(options.headers);
		}

		for (const [key, value] of rawHeaders) {
			outputHeaders.push([key, value.toString()]);
		}
	}

	if (options?.bearer) {
		outputHeaders.push(["Authorization", `Bearer ${options.bearer}`]);
	}

	let body: string | undefined;
	if (options?.body) {
		body = options.body;
	}
	if (options?.json) {
		body = JSON.stringify(options.json);
		outputHeaders.push(["accept", "application/json"]);
		outputHeaders.push(["Content-Type", "application/json"]);
	}

	const final = async (method: FetchMethod) => {
		const res = await fetch(finalUrl, {
			body: body,
			headers: outputHeaders,
			method: method,
			signal: options?.abort_signal,
		});

		const raw = await res.arrayBuffer();
		let rawString: string | undefined;
		try {
			rawString = new TextDecoder().decode(raw);
		} catch {
			/* empty */
		}
		let outputBody: unknown | null = null;
		try {
			if (rawString) {
				outputBody = JSON.parse(rawString);
			}
		} catch {
			/* empty */
		}

		return {
			body: outputBody,
			ok: res.ok,
			raw_body: rawString ?? raw,
			status: res.status,
		};
	};

	const get = async () => {
		return await final("GET");
	};

	const post = async () => {
		return await final("POST");
	};

	const put = async () => {
		return await final("PUT");
	};

	const del = async () => {
		return await final("DELETE");
	};

	const patch = async () => {
		return await final("PATCH");
	};

	return {
		delete: del,
		get: get,
		patch: patch,
		post: post,
		put: put,
	};
}
function serializeKeyValues(
	keyValues: [SerializableKey | null | undefined, SerializableValue | null | undefined][],
): [SerializableKey, SerializableValue][] {
	const out: [SerializableKey, SerializableValue][] = [];
	for (let i = 0; i < keyValues.length; i++) {
		const [key, value] = keyValues[i];

		// Handle nullish values
		if (key == null || key === "" || value == null || value === "") {
			continue;
		}

		// Serialize
		out.push([key.toString(), value.toString()]);
	}

	return out;
}

async function logAndContinue(f: () => Promise<unknown>) {
	try {
		const r = await f();
		console.info("Success: ", r);
	} catch (error) {
		console.error(error);
	}
}

function test() {
	// Test if an invalid url can be passed: Should error
	logAndContinue(async () => await clientFetch(url`hey there ${"a"}`).get());

	// Test if clientFetch works under normal circumstances: Should not error
	logAndContinue(
		async () =>
			await clientFetch(url`http://localhost:8000/shop/concept-order`, {
				json: {
					order_dump: {
						SomeKey: "SomeVal",
					},
					order_type: "domain",
				},
			}).post(),
	);
	// Test if the url is encoded correctly: Should not error
	logAndContinue(() => {
		const apiUrl = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free";
		const urlParamValue = "test@test.com/5%";
		const encodedUrl = url`${apiUrl}/test space?test_value=${urlParamValue}&manual_param=manu@l+value`;

		const expectedUrl =
			"https://api.pdok.nl/bzk/locatieserver/search/v3_1/free/test%20space?test_value=test%40test.com%2F5%25&manual_param=manu%40l%2Bvalue";
		const isMatching = encodedUrl.inner === expectedUrl;
		const result = `Result: ${encodedUrl.inner}; Expected: ${expectedUrl}; isMatching:${isMatching};`;

		// If the values are not matching, throw an error to indicate that the test failed
		if (!isMatching) {
			throw new Error(result);
		}
		// Return the result to indicate that the test passed
		return result;
	});
}
